import type { QuestionFeedbackPublic } from "@/client"
import { Badge, Box, Button, Card, HStack, Stack, Text } from "@chakra-ui/react"
import { useState } from "react"
import { HtmlContent } from "../Common/HtmlContent"
import { FeedbackSubmitForm } from "./FeedbackSubmitForm"

interface FeedbackThreadProps {
  feedback: QuestionFeedbackPublic
  isTeacher: boolean
  onReply?: (feedbackText: string, isPublished: boolean) => Promise<void>
  onEdit?: (feedbackText: string) => Promise<void>
  onDelete?: () => Promise<void>
  onResolve?: () => Promise<void>
}

export function FeedbackThread({
  feedback,
  isTeacher,
  onReply,
  onEdit,
  onDelete,
  onResolve,
}: FeedbackThreadProps) {
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canEdit = feedback.is_own && feedback.status !== "resolved"
  const canDelete = feedback.is_own && feedback.status !== "resolved"

  const handleReply = async (feedbackText: string, isPublished = false) => {
    if (!onReply) return
    setIsSubmitting(true)
    try {
      await onReply(feedbackText, isPublished)
      setShowReplyForm(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = async (feedbackText: string) => {
    if (!onEdit) return
    setIsSubmitting(true)
    try {
      await onEdit(feedbackText)
      setShowEditForm(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete || !confirm("Are you sure you want to delete this feedback?"))
      return
    setIsSubmitting(true)
    try {
      await onDelete()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResolve = async () => {
    if (!onResolve) return
    setIsSubmitting(true)
    try {
      await onResolve()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card.Root>
      <Card.Body>
        <Stack gap={3}>
          {/* Header */}
          <HStack justify="space-between">
            <HStack>
              <Badge colorPalette={getStatusColor(feedback.status)}>
                {feedback.status}
              </Badge>
              {feedback.is_own && (
                <Badge colorPalette="green">Your Feedback</Badge>
              )}
              {feedback.is_published && (
                <Badge colorPalette="blue">Published</Badge>
              )}
            </HStack>
            {isTeacher && feedback.created_by_name && (
              <Text fontSize="sm" color="gray.600">
                By: {feedback.created_by_name}
              </Text>
            )}
          </HStack>

          {/* Feedback Content */}
          {showEditForm ? (
            <FeedbackSubmitForm
              initialValue={feedback.feedback_text}
              onSubmit={handleEdit}
              onCancel={() => setShowEditForm(false)}
              isSubmitting={isSubmitting}
              submitLabel="Update Feedback"
            />
          ) : (
            <HtmlContent
              content={feedback.feedback_text}
              css={{ "& p": { marginBottom: "0.5rem" } }}
            />
          )}

          {/* Actions */}
          {!showEditForm && (
            <HStack gap={2}>
              {isTeacher && onReply && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowReplyForm(!showReplyForm)}
                >
                  {showReplyForm ? "Cancel Reply" : "Reply"}
                </Button>
              )}
              {canEdit && onEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowEditForm(true)}
                  disabled={isSubmitting}
                >
                  Edit
                </Button>
              )}
              {canDelete && onDelete && (
                <Button
                  size="sm"
                  variant="outline"
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  Delete
                </Button>
              )}
              {isTeacher && onResolve && feedback.status !== "resolved" && (
                <Button
                  size="sm"
                  colorPalette="green"
                  onClick={handleResolve}
                  disabled={isSubmitting}
                >
                  Mark Resolved
                </Button>
              )}
            </HStack>
          )}

          {/* Reply Form */}
          {showReplyForm && onReply && (
            <Box mt={2} pl={4} borderLeft="2px solid" borderColor="gray.200">
              <FeedbackSubmitForm
                onSubmit={handleReply}
                onCancel={() => setShowReplyForm(false)}
                isSubmitting={isSubmitting}
                submitLabel="Send Reply (Private)"
                showPublishButton={true}
              />
            </Box>
          )}

          {/* Replies */}
          {feedback.replies && feedback.replies.length > 0 && (
            <Stack gap={2} pl={4} borderLeft="2px solid" borderColor="gray.200">
              {feedback.replies.map((reply) => (
                <Card.Root key={reply.id} size="sm">
                  <Card.Body>
                    <Stack gap={2}>
                      <HStack justify="space-between">
                        <HStack>
                          {reply.is_published && (
                            <Badge colorPalette="blue" size="sm">
                              Published
                            </Badge>
                          )}
                          {isTeacher && reply.created_by_name && (
                            <Text fontSize="xs" color="gray.600">
                              {reply.created_by_name}
                            </Text>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {new Date(reply.created_at).toLocaleDateString()}
                        </Text>
                      </HStack>
                      <HtmlContent
                        content={reply.feedback_text}
                        fontSize="sm"
                      />
                    </Stack>
                  </Card.Body>
                </Card.Root>
              ))}
            </Stack>
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}

function getStatusColor(status: string): string {
  switch (status) {
    case "unread":
      return "yellow"
    case "read":
      return "blue"
    case "resolved":
      return "green"
    default:
      return "gray"
  }
}
